import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css';

function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'features', 'use-cases', 'pricing', 'security', 'about'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="landing-page">
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-icon">⚙️</span>
            <span className="logo-text">IndustrialIQ</span>
          </div>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a 
              href="#features" 
              className={activeSection === 'features' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}
            >
              Features
            </a>
            <a 
              href="#pricing" 
              className={activeSection === 'pricing' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}
            >
              Pricing
            </a>
            <a 
              href="#security" 
              className={activeSection === 'security' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('security'); }}
            >
              Security
            </a>
            <a 
              href="#about" 
              className={activeSection === 'about' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}
            >
              About
            </a>
          </div>

          <div className="nav-cta">
            <Link to="/login" className="nav-signin">Sign In</Link>
            <Link to="/register" className="nav-trial-btn">Get Started</Link>
          </div>
        </div>
      </nav>

      <div className="hero-banner">
        <img 
          src="/industrialiqbanner_1767664264447.jpg" 
          alt="IndustrialIQ - AI-Powered Document Intelligence for Industrial Operations"
          className="hero-banner-image"
        />
      </div>

      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-headline">Stop Searching. Start Answering.</h1>
            <p className="hero-subtext">Teams can save 100+ hours per week with AI-powered document intelligence</p>
            <p className="hero-copy">
              Securely Upload your technical manuals, safety procedures, and equipment guides. 
              Ask questions in plain language. Get instant answers with exact source citations—no 
              more digging through hundreds of pages.
            </p>
            <div className="hero-ctas">
              <Link to="/register" className="cta-primary">Get Started</Link>
              <a href="#features" className="cta-secondary" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>
                Learn More →
              </a>
            </div>
            <div className="hero-trust-badge">
              <span>✓ Enterprise-grade security</span>
              <span>✓ 99.9% uptime</span>
              <span>✓ Flexible pricing plans</span>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-mockup">
              <div className="mockup-header">
                <span className="mockup-dot"></span>
                <span className="mockup-dot"></span>
                <span className="mockup-dot"></span>
              </div>
              <div className="mockup-content">
                <div className="mockup-question">
                  <strong>Q:</strong> What are the safety procedures for kiln shutdown?
                </div>
                <div className="mockup-answer">
                  <strong>A:</strong> The kiln shutdown procedure requires following a 3-step sequence: 
                  First, reduce fuel flow to 25% over 30 minutes. Second, maintain airflow at minimum 
                  levels. Third, monitor temperature descent...
                  <div className="mockup-source">
                    📄 Source: Safety_Manual_2024.pdf (Page 47)
                    <span className="mockup-confidence">Confidence: 95%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="problem-solution-section">
        <div className="container">
          <h2 className="section-title">The Challenge Industrial Teams Face</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">⏱️</div>
              <h3>Hours Lost Searching</h3>
              <p>Maintenance teams spend 5-10 hours weekly searching through technical documentation for answers.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">⚠️</div>
              <h3>Safety & Compliance Risks</h3>
              <p>Inconsistent access to safety procedures increases compliance violations and incident risk.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">👥</div>
              <h3>Slow Employee Onboarding</h3>
              <p>New hires take weeks to become productive without quick access to operational knowledge.</p>
            </div>
          </div>
          <div className="solution-callout">
            <h3>IndustrialIQ Solves This in Minutes</h3>
            <p>One platform. All your documents. Instant answers. Your team gets answers in seconds, not hours.</p>
          </div>
        </div>
      </section>

      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">Powerful Features Built for Industrial Operations</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📄</div>
              <h3>Smart Document Processing</h3>
              <p>Upload PDF, Word, text, markdown, and CSV files up to 100MB each. Automatic extraction and intelligent organization into custom folders.</p>
              <div className="feature-benefit">Organize 500+ documents in minutes</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>Intelligent Q&A with Claude AI</h3>
              <p>Ask questions in everyday language. Powered by Claude AI Sonnet 4.5. Every answer includes exact source citations and confidence scores.</p>
              <div className="feature-benefit">Get answers in 2-5 seconds</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Complete Privacy & Security</h3>
              <p>Your documents stay private—never used to train AI models. Enterprise-grade encryption. 30-day data retention (7 days by Sept 2025).</p>
              <div className="feature-benefit">Bank-level security</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Usage Tracking & Control</h3>
              <p>Monitor monthly usage, storage, document count, and questions asked. Stay within plan limits with full visibility.</p>
              <div className="feature-benefit">No surprise charges</div>
            </div>
          </div>
        </div>
      </section>

      <section id="use-cases" className="use-cases-section">
        <div className="container">
          <h2 className="section-title">Built for Every Role in Your Operation</h2>
          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-icon">👨‍💼</div>
              <h3>Plant Managers</h3>
              <p>Quick access to operational procedures and safety protocols</p>
              <div className="use-case-example">Find compliance requirements in seconds</div>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon">🔧</div>
              <h3>Maintenance Teams</h3>
              <p>Troubleshooting steps and equipment specifications instantly</p>
              <div className="use-case-example">30 seconds vs 30 minutes finding procedures</div>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon">🛡️</div>
              <h3>Safety Officers</h3>
              <p>Verify compliance requirements and safety procedures</p>
              <div className="use-case-example">Always have the latest safety protocols</div>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon">📚</div>
              <h3>Training Coordinators</h3>
              <p>Help new employees find answers in technical documentation</p>
              <div className="use-case-example">Reduce onboarding time by 50%</div>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon">⚙️</div>
              <h3>Operations Staff</h3>
              <p>Get immediate answers without searching through pages</p>
              <div className="use-case-example">Work faster, more confidently</div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that fits your team size. Upgrade or downgrade anytime.</p>
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Starter</h3>
                <div className="pricing-price">
                  <span className="price-currency">$</span>
                  <span className="price-amount">29</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="pricing-description">Perfect for small teams</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 100,000 AI tokens/month</li>
                <li>✓ ~50-100 detailed questions</li>
                <li>✓ 5GB document storage</li>
                <li>✓ Up to 50 documents</li>
                <li>✓ Full Q&A with citations</li>
                <li>✓ Conversation history</li>
                <li>✓ Email support</li>
              </ul>
              <Link to="/register" className="pricing-cta">Get Started</Link>
            </div>

            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <div className="pricing-header">
                <h3>Professional</h3>
                <div className="pricing-price">
                  <span className="price-currency">$</span>
                  <span className="price-amount">99</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="pricing-description">Ideal for departments</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 500,000 AI tokens/month</li>
                <li>✓ ~250-500 detailed questions</li>
                <li>✓ 50GB document storage</li>
                <li>✓ Up to 500 documents</li>
                <li>✓ Everything in Starter</li>
                <li>✓ Priority support</li>
                <li>✓ Advanced folder organization</li>
              </ul>
              <Link to="/register" className="pricing-cta">Get Started</Link>
            </div>

            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Enterprise</h3>
                <div className="pricing-price">
                  <span className="price-currency">$</span>
                  <span className="price-amount">299</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="pricing-description">For large operations</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 2,000,000 AI tokens/month</li>
                <li>✓ ~1,000-2,000 questions</li>
                <li>✓ 200GB document storage</li>
                <li>✓ Unlimited documents</li>
                <li>✓ Everything in Professional</li>
                <li>✓ Dedicated support</li>
                <li>✓ Custom integrations</li>
              </ul>
              <Link to="/register" className="pricing-cta">Get Started</Link>
            </div>
          </div>
          <p className="pricing-note">
            All plans include secure user accounts, mobile-friendly interface, and self-service management. 
            Overage billed at cost + 10% markup.
          </p>
        </div>
      </section>

      <section id="security" className="security-section">
        <div className="container">
          <h2 className="section-title">Enterprise-Grade Security You Can Trust</h2>
          <div className="security-grid">
            <div className="security-card">
              <div className="security-icon">🔐</div>
              <h3>Data Privacy Guaranteed</h3>
              <p>Your documents are never used to train AI models. Claude API commercial terms ensure complete confidentiality.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">🛡️</div>
              <h3>End-to-End Encryption</h3>
              <p>All data encrypted in transit and at rest. Bank-level security standards for your sensitive documentation.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">👤</div>
              <h3>Complete User Isolation</h3>
              <p>Multi-tenant architecture ensures your data is completely separated from other users. No cross-access possible.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">📋</div>
              <h3>Compliance Ready</h3>
              <p>30-day data retention (reducing to 7 days in Sept 2025). Designed for industrial compliance requirements.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">About IndustrialIQ</h2>
          <div className="about-content">
            <div className="about-story">
              <h3>Our Mission</h3>
              <p>
                IndustrialIQ was built to solve a critical problem in industrial operations: teams waste countless 
                hours searching through technical documentation for answers they need right now. We believe that in 
                high-stakes environments like cement plants, mining operations, and metallurgical facilities, having 
                instant access to accurate information isn't just convenient—it's essential for safety, efficiency, 
                and compliance.
              </p>
              <p>
                Our platform combines cutting-edge AI technology with deep understanding of industrial workflows to 
                deliver a document intelligence solution that's both powerful and easy to use. Whether you're a 
                maintenance technician troubleshooting equipment at 2 AM or a safety officer verifying compliance 
                procedures, IndustrialIQ gives you the answers you need in seconds, not hours.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat-card">
                <div className="stat-number">10+</div>
                <div className="stat-label">Hours Saved Per Week</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">95%</div>
                <div className="stat-label">Average Confidence Score</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">2-5 sec</div>
                <div className="stat-label">Average Response Time</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">100%</div>
                <div className="stat-label">Data Privacy Guaranteed</div>
              </div>
            </div>
            <div className="about-technology">
              <h3>Powered by Advanced AI</h3>
              <p>
                We use Claude Sonnet 4.5, one of the most advanced AI models available, to understand your questions 
                and provide accurate answers. Our system combines semantic search with retrieval-augmented generation 
                (RAG) to ensure every answer is grounded in your actual documents, with exact source citations so you 
                can verify the information.
              </p>
              <p>
                Your data security is our top priority. Unlike many AI services, your documents are never used to 
                train AI models. We maintain enterprise-grade encryption, multi-tenant isolation, and comply with 
                industrial data retention requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Document Intelligence?</h2>
            <p>Join industrial teams saving 10+ hours per week. Get started today.</p>
            <div className="cta-buttons">
              <Link to="/register" className="cta-primary large">Get Started</Link>
              <Link to="/login" className="cta-secondary large">Sign In to Your Account</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <span className="logo-icon">⚙️</span>
              <span className="logo-text">IndustrialIQ</span>
              <p>AI-Powered Document Intelligence for Industrial Operations</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>Features</a>
                <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>Pricing</a>
                <a href="#security" onClick={(e) => { e.preventDefault(); scrollToSection('security'); }}>Security</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#use-cases" onClick={(e) => { e.preventDefault(); scrollToSection('use-cases'); }}>Use Cases</a>
                <Link to="/login">Sign In</Link>
                <Link to="/register">Get Started</Link>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <a href="mailto:michael@process-iq.com.au">michael@process-iq.com.au</a>
                <p style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '8px' }}>For subscriptions & inquiries</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 IndustrialIQ. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
